import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface IProgress {
    percent: number;
    kb: number;
    determinate?: boolean;
}

// default progress is at 0
export const DEFAULT_PROGRESS: IProgress = {
    percent: 0,
    kb: 0,
    determinate: true
};


/** A service to update multiple plots at once with observables */
@Injectable({ providedIn: 'root' })
export class LoadingProgressService {
    /*
     * Create a map for quick lookups
     * key: plot id
     * value: list of active progress trackers
     */
    plotLoadingTrackers: Map<string, LoadingProgressTracker[]> = new Map();
    plotLoadingProgress: Map<string, BehaviorSubject<IProgress>> = new Map();

    /** add a new progress tracker to a plot's list of trackers. If no trackers exist for a plot initialize a new list */
    addNewTracker( plotId: string, tracker: LoadingProgressTracker ) {
        if ( this.plotLoadingTrackers.has( plotId ) ) {
            this.plotLoadingTrackers.set( plotId, [ ...this.plotLoadingTrackers.get( plotId ), tracker ] );
        } else {
            this.plotLoadingTrackers.set( plotId, [ tracker ] );
        }
        this.updateTrackerProgress( plotId );
    }

    /** delete the progres tracker for a plot */
    removeTracker( plotId: string ) {
        this.plotLoadingProgress.delete( plotId );
        this.plotLoadingTrackers.delete( plotId );
    }

    /** trigger this when we recieve new http packets to update the current loading state */
    updateAllTrackersProgress() {
        const keys = Array.from( this.plotLoadingTrackers.keys() );
        keys.forEach( key => this.updateTrackerProgress( key ) );
    }

    /** calculates the total loading progress for a plot */
    updateTrackerProgress( plotId: string ) {
        const trackers = this.plotLoadingTrackers.get( plotId );
        if ( this.plotLoadingProgress.has( plotId ) ) {
            this.plotLoadingProgress.get( plotId ).next( updateMainLoadingProgress( trackers ) );
        } else {
            this.plotLoadingProgress.set( plotId, new BehaviorSubject( updateMainLoadingProgress( trackers ) ));
        }
    }

    /** returns an observable that emits when we update the loading progress of a plot */
    getLoadingProgress$( plotId: string ) {
        return this.plotLoadingProgress.get( plotId );
    }

    /** return the current list of loading trackers registered to a plot */
    getLoadingTrackers( plotId: string ) {
        return this.plotLoadingTrackers.get( plotId );
    }
}


/** calculate the value of the loading bar based on the progress of each dataset in the plot */
function updateMainLoadingProgress( loadingTrackers: LoadingProgressTracker[] ): IProgress {
    const newProgress: IProgress = {
        percent: 0,
        kb: 0,
        determinate: true
    };
    // if none of our values are null, use a determinate loading bar
    // otherwise use an indeterminate loading bar
    newProgress.determinate = loadingTrackers.every( tracker => {
        newProgress.kb += tracker.progress.kb;
        newProgress.percent += tracker.progress.percent;
        return tracker.progress.percent !== null;
    });
    newProgress.percent /=  loadingTrackers.length; // average the percent
    return newProgress;
}

/**
 * This class tracks the progress made by an http request.
 * It will make a best guess based on current data for how much data is left to download
 */
export class LoadingProgressTracker {
    progress: IProgress = {
        percent: 0,
        kb: 0,
        determinate: true
    };
    private _rangeIsValid: boolean;

    constructor( public start: number, public end: number, public offset = 0, public progressCallback = () => {}, public xIndex = 0 ) {
        this._rangeIsValid = !isNaN( start ) && !isNaN( end );
        if ( this._rangeIsValid ) {
            start += offset;
            end += offset;
        }
    }

    setFullProgress() {
        this.progress.percent = 100;
    }

    /** when events return from the server, we use this function to estimate our progress */
    onProgressHandler( evt ) {
        // type 4 is download complete event
        if ( evt.type === 4 ) {
            this.progress.percent = 100;
        }
        // type 3 is a download progress event
        if ( evt.type !== 3 ) {
            return;
        }
        // Track the total kb loaded
        this.progress.kb = Math.round( evt.loaded / 1024 );

        this.progress.percent = null;
        if ( evt.total ) {
            // If the server sent the total number of bytes in the request, compute the percent based on that
            this.progress.percent = 100 * evt.loaded / evt.total;
        } else if ( this._rangeIsValid && evt.partialText ) {
            // If latis doesn't know the total amount of data it will send, we can get the latest x-axis value sent
            // and compare that to the range of data that was requested. There can be data gaps or varying data
            // cadences, so this isn't always an accurate representation of the percent of data loaded, but it's
            // better than nothing.

            // We need to get the last instance of something that looks like "[1481439764570,". This should be the
            // farthest value loaded so far. There's no function to perform a regex search from the end of a
            // string, and we don't want to perform a regex search on the whole response in order to find only the
            // last match (the response could be on the order of many MB), so we'll search backwards until we find
            // a matching pattern, but we'll limit our search to 10,000 characters.

            const responseText = evt.partialText; // shorthand
            const responseLength = responseText.length;
            // max index of 0 in case the response is under 10000 characters
            const searchLimit = Math.max( responseLength - 10000, -1 );
            let index = responseLength;
            let match = null;

            // search from the end of the string to find '['
            while (
              match === null
              // if '[' is the first character of the string, lastIndexOf will never return -1, despite the value of
              // its second parameter. The condition below catches the case where we're already searched through the
              // entire string
              && index > 0
              // jump to the next instance of '[' closer to the beginning of the string
              && ( index = responseText.lastIndexOf('[', index - 1 )) > searchLimit ) {

                // we're going to make an assumption here that the xIndex that we are searching for is not the last item in the array
                // this allows us to generalize matching based on the expected xIndex. We will have an edge case if the value we search
                // for exists in other elements of the array. However this function is meant to be a approximation anyways.
                // we can't check that something is the last item because we are searching this array as a string and can't do array.length

                // first element in array
                if ( this.xIndex === 0 ) {
                    // regex search for a string that looks like "[12345," or "[-12345,". Only match at the beginning
                    // of the substring, because by the nature of how we're searching, we've already searched through
                    // the rest of the string.
                    match = responseText.substring( index ).match(/^\[-?\d+,/);
                } else if ( this.xIndex > 0 ) {
                    // events data returns arrays with the parameters [..., xValue, ...] and we
                    // want to get the value of xValue
                    match = responseText.substring( index ).match(/(\d+),/g);
                } else {
                    // something wrong... the xIndex wasn't set right.
                    return;
                }

            }

            if ( match !== null ) {
                let latestValue;
                if ( this.xIndex === 0 ) {
                    // chop the first and last characters off the string ("[" and ",") and parse it as an integer
                    latestValue = parseInt( match[0].substr( 1, match[0].length - 2 ), 10 );
                } else if ( this.xIndex > 0 ) {
                    // get the number matched by parens and parse as an int
                    latestValue = parseInt( match[ this.xIndex ], 10 );
                    // the latest value can be before the start if the server returned an event
                    // which started before this.start, but ends either during or after our requested range
                    if ( latestValue < this.start ) {
                        latestValue = null;
                    }
                }

                if ( latestValue !== null ) {
                    this.progress.percent = 100 * ( latestValue - this.start ) / ( this.end - this.start );
                }
            }
        }

        this.progressCallback();
    }
}
